import Constants from '@/Constants';
import { useProgramClass } from '@/hooks/useProgramClass';
import { Workshop, WorkshopTip } from '@/models/Workshop';
import { ApplicationState } from '@/store';
import React, { memo, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

interface Props {
    onChange: (tips: WorkshopTip[]) => void;
}

const WorkshopTips: React.FC<Props> = ({ onChange }: Props) => {
    const { courseId: courseIdString, classId: classIdString } = useParams<{ courseId: string; classId: string }>();

    const courseId = Number(courseIdString);
    const classId = Number(classIdString);

    const strings = useSelector((state: ApplicationState) => state.locale.data);

    const [tips, setTips] = useState<WorkshopTip[]>([]);

    const { programClass } = useProgramClass({
        programClassId: classId,
        withEnrollmentsCount: true,
        withWorkshopContent: true,
    });

    const workshopProgramClass: Workshop | undefined = programClass?.workshops && programClass.workshops[0];

    const workshop: Workshop | undefined = !!courseId
        ? programClass?.workshops?.find((workshop: Workshop) => workshop.course.id === courseId)
        : workshopProgramClass;

    const resetTipOrder = (tips: WorkshopTip[]) => {
        tips.forEach((tip: WorkshopTip, index: number) => (tip.workshopTipOrder = index));
    };

    const reorder = (list: WorkshopTip[], startIndex: number, endIndex: number): WorkshopTip[] => {
        const result: WorkshopTip[] = Array.from(list);
        const [removed] = result.splice(startIndex, 1);

        result.splice(endIndex, 0, removed);
        resetTipOrder(result);
        return result;
    };

    const handleOnReorderTips = (result: DropResult): void => {
        const { source, destination } = result;

        if (destination) {
            setTips(reorder(tips, source.index, destination.index));
        }
    };

    const handleOnRemoveTips = (index: number) => {
        const tipsAux: WorkshopTip[] = [...tips];

        tipsAux.splice(index, 1);
        resetTipOrder(tipsAux);
        setTips(tipsAux);
    };

    const handleOnUpdateTips = (tip: WorkshopTip, index: number, value: string) => {
        const tipsAux: WorkshopTip[] = [...tips];

        tipsAux.splice(index, 1, { ...tip, description: value });
        setTips(tipsAux);
    };

    const handleAddNewTips = () => {
        const newTips: WorkshopTip[] = [...tips, { description: '' } as WorkshopTip];

        resetTipOrder(newTips);
        setTips(newTips);
    };

    useEffect(() => setTips(workshop?.tips || []), [workshop]);

    useEffect(() => onChange(tips), [tips]);

    return (
        <div className="tips-container">
            <div className="container-workshop-tips">
                <div className="container-workshop-tips__info">
                    <span className="input-section-info">{strings.WORKSHOP_TIPS_INFO}</span>
                    <OverlayTrigger
                        overlay={
                            <Tooltip id="team-closed-hint">
                                <span dangerouslySetInnerHTML={{ __html: strings.WORKSHOP_TIPS_ALERT }} />
                            </Tooltip>
                        }
                        placement="bottom"
                    >
                        <i className="hint material-icons-outlined icon">{Constants.icons.ERROR_OUTLINE}</i>
                    </OverlayTrigger>
                </div>

                <DragDropContext onDragEnd={handleOnReorderTips}>
                    <Droppable droppableId="droppable">
                        {(provided) => (
                            <div {...provided.droppableProps} className="container-workshop-tips__tips-container" ref={provided.innerRef}>
                                {tips.length > 0 &&
                                    tips.map((tip: WorkshopTip, index: number) => (
                                        <Draggable draggableId={index.toString()} index={index} key={index.toString()}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    style={provided.draggableProps.style}
                                                >
                                                    <div className="container-workshop-tips__tips-input">
                                                        <i className="material-icons-outlined align-middle input-icon drag-icon">
                                                            {Constants.icons.DRAG_INDICATOR}
                                                        </i>
                                                        <div className="form-group inner-addon container-workshop-tips__tips-box">
                                                            <i
                                                                className="material-icons-outlined align-middle input-icon trash-icon"
                                                                onClick={() => handleOnRemoveTips(index)}
                                                            >
                                                                {Constants.icons.TRASH_CAN}
                                                            </i>
                                                            <input
                                                                className="form-control"
                                                                maxLength={255}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                    handleOnUpdateTips(tip, index, e.target.value)
                                                                }
                                                                placeholder={strings.WORKSHOP_TIPS_PLACEHOLDER}
                                                                type="text"
                                                                value={tip.description}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <button className="btn container-workshop-tips__add-tips" onClick={handleAddNewTips} type="button">
                    <i className="material-icons-outlined align-middle">{Constants.icons.CIRCLE_PLUS}</i>
                    {strings.WORKSHOP_ADD_TIPS_TITLE}
                </button>
            </div>
        </div>
    );
};

export default memo(WorkshopTips);
